import { useState, useEffect, useRef } from 'react';
import * as pdfjs from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { trackError } from 'Utils/errorMonitoring';
import { useTranslation } from 'react-i18next';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const LOAD_TIMEOUT_MS = 30_000;
const MAX_PASSWORD_ATTEMPTS = 3;
const NO_RESULT = { fileKey: null, pdf: null, hasError: false };

// Las URLs de los archivos van firmadas y la firma cambia en cada respuesta de la API:
// lo que identifica al archivo es la URL sin la query.
const getFileKey = (url) => {
    if (!url) return null;
    return url.split('?')[0];
};

export default function usePDFLoader(url) {
    const fileKey = getFileKey(url);
    const [result, setResult] = useState(NO_RESULT);

    const { t } = useTranslation('common');

    // Se leen al empezar la carga, pero que cambien no debe reiniciarla.
    const latestRef = useRef({ url, t });
    latestRef.current = { url, t };

    useEffect(() => {
        if (!fileKey) return undefined;

        let cancelled = false;
        let timeoutId = null;

        const fail = () => setResult({ ...NO_RESULT, fileKey, hasError: true });

        const loader = pdfjs.getDocument({
            url: latestRef.current.url,
            disableFontFace: false,
            useSystemFonts: false,
            verbosity: 0,
        });

        let passwordAttempts = 0;

        loader.onPassword = (callback, reason) => {
            if (cancelled) return;

            switch (reason) {
                case pdfjs.PasswordResponses.NEED_PASSWORD: {
                    const password = prompt(
                        latestRef.current.t('please-enter-the-password-to-open-this-pdf-file-press-cancel-to-exit'),
                    );
                    if (password === null) {
                        fail();
                        loader.destroy();
                        return;
                    }
                    passwordAttempts++;
                    callback(password);
                    break;
                }
                case pdfjs.PasswordResponses.INCORRECT_PASSWORD: {
                    if (passwordAttempts >= MAX_PASSWORD_ATTEMPTS) {
                        fail();
                        loader.destroy();
                        return;
                    }
                    const password = prompt(latestRef.current.t('invalid-password-please-try-again'));
                    if (password === null) {
                        fail();
                        loader.destroy();
                        return;
                    }
                    passwordAttempts++;
                    callback(password);
                    break;
                }
                default:
                    break;
            }
        };

        async function loadPdf() {
            try {
                const timeoutPromise = new Promise((_, reject) => {
                    timeoutId = setTimeout(
                        () => reject(new Error(`Error: PDF load timeout after ${LOAD_TIMEOUT_MS}ms, URL: ${fileKey}`)),
                        LOAD_TIMEOUT_MS,
                    );
                });
                const pdf = await Promise.race([loader.promise, timeoutPromise]);

                if (cancelled) return;

                setResult({ ...NO_RESULT, fileKey, pdf });
            } catch (error) {
                if (cancelled) return;

                fail();
                trackError(error);
            } finally {
                clearTimeout(timeoutId);
            }
        }

        loadPdf();

        // Destruir la tarea de carga destruye también el documento. Se olvida el resultado para
        // no devolver un documento ya destruido si se vuelve a este mismo archivo.
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            loader.destroy();
            setResult(NO_RESULT);
        };
    }, [fileKey]);

    // Se decide al renderizar y no en un efecto: en cuanto cambia el archivo, el documento
    // anterior deja de devolverse y nunca se ve junto a los datos de otra factura.
    const isCurrent = result.fileKey === fileKey;

    return {
        pdf: isCurrent ? result.pdf : null,
        isLoading: Boolean(fileKey) && !isCurrent,
        hasError: isCurrent && result.hasError,
    };
}
